import React, { FC, useEffect } from 'react';
import { Button, Form, InputNumber, Modal } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { IFormValue } from 'common/models';
import { PRICE_LIMIT } from 'common/config';
import { replaceCommaWithPeriod } from 'common/helpers';
import { Bin } from 'common/svg';
import { ISubdivision } from 'entities/Subdivision/Subdivision.models';
import { calculateSubdivisionBalance } from 'entities/Subdivision/Subdivision.helper';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  subdivision: ISubdivision | null;
  onCancel: () => void;
  onSubmit: (payload: { balance?: number | null; maxGoodPrice?: number | null }, onSuccess: () => void) => void;
}

export const SubdivisionLimitsChangeModal: FC<IComponentProps> = ({ open, loading, subdivision, onCancel, onSubmit }) => {
  const [form] = useForm();

  useEffect(() => {
    if (open && subdivision) {
      const balance = calculateSubdivisionBalance(subdivision);

      form.setFieldsValue({
        balance: balance ? Number(balance.toFixed(2)) : null,
        maxGoodPrice: subdivision.maxGoodPrice,
      });
    }
  }, [open, subdivision]);

  const handleCancel = () => {
    if (loading) {
      return;
    }

    onCancel();
    form.resetFields();
  };

  const handleSubmit = ({ balance, maxGoodPrice }: IFormValue) => {
    onSubmit(
      {
        balance: balance ? Number(balance) : null,
        maxGoodPrice: maxGoodPrice ? Number(maxGoodPrice) : null,
      },
      handleCancel,
    );
  };

  return (
    <Modal
      rootClassName="redesign modal"
      width={420}
      centered
      open={open}
      onCancel={handleCancel}
      destroyOnClose
      closeIcon={false}
      footer={[
        <Button key="cancel" className="button-sm secondary" onClick={handleCancel} loading={loading}>
          Отмена
        </Button>,
        <Button key="submit" className="button-sm primary" onClick={form.submit} loading={loading}>
          Сохранить
        </Button>,
      ]}
    >
      <div className="mb-3 ff-medium fs-m">Изменение лимитов на закупку</div>

      <div className="mb-6 ff-regular fs-xs color-dark-grey">
        Вы можете редактировать только остаток на текущий период — общий лимит изменится автоматически.
      </div>

      <Form form={form} requiredMark={false} layout="vertical" onFinish={handleSubmit}>
        <div className="flex align-end gap-4">
          <Form.Item className="flex-1" label="Остаток за период" name="balance">
            <InputNumber
              placeholder="0.00"
              max={PRICE_LIMIT}
              suffix="₽"
              controls={false}
              precision={2}
              parser={replaceCommaWithPeriod}
              formatter={replaceCommaWithPeriod}
            />
          </Form.Item>

          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const balance = getFieldValue('balance');

              if (balance === null) {
                return null;
              }

              return (
                <div className="mb-4">
                  <Button
                    className="button-icon"
                    icon={<Bin className="icon-bin-pale-red" />}
                    onClick={() => form.setFieldValue('balance', null)}
                  />
                </div>
              );
            }}
          </Form.Item>
        </div>

        <div className="flex align-end gap-4">
          <Form.Item className="flex-1" label="Лимит цены на товар (включительно)" name="maxGoodPrice">
            <InputNumber
              placeholder="0.00"
              max={PRICE_LIMIT}
              suffix="₽"
              controls={false}
              precision={2}
              parser={replaceCommaWithPeriod}
              formatter={replaceCommaWithPeriod}
            />
          </Form.Item>

          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const maxGoodPrice = getFieldValue('maxGoodPrice');

              if (maxGoodPrice === null) {
                return null;
              }

              return (
                <div className="mb-4">
                  <Button
                    className="button-icon"
                    icon={<Bin className="icon-bin-pale-red" />}
                    onClick={() => form.setFieldValue('maxGoodPrice', null)}
                  />
                </div>
              );
            }}
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};
